import { Component, h, Host, Prop, Element, State, Fragment } from '@stencil/core';
import { storage } from '../../../services';
import {
    ChatbotAnswerInterface,
    ChatbotDialogOptionInterface,
    ChatbotEscalationFeedbackInterface,
    ChatbotEscalationInterface,
    ChatBotService,
    ChatbotTextInterface
} from '../../../services/chatbot';
import { WebChatbotListCustomEvent } from '../../../components';

@Component({
    tag: 'web-chatbot',
    styleUrls: ['chatbot.scss'],
    shadow: false
})
export class Chatbot {
    @Prop() placeholderText = '';
    @Prop() submitButtonText = '';
    @Prop() closeButtonText = '';
    @Prop() chatbotAriaLabelOpen = '';
    @Prop() maxLengthText = '';
    @Prop() maxlength = 150;
    @Prop({ mutable: true }) submitDisabled = false;
    @Prop() mode: 'normal' | 'small' | 'portable' = 'normal';
    @Prop() features: string;

    @Element() host: HTMLWebChatbotElement;

    @State() isDesktop = false;

    private chatbotService: ChatBotService;
    private chatBotList: (ChatbotAnswerInterface | string)[] = [];
    private escalation: ChatbotEscalationInterface;
    private texts: ChatbotTextInterface;
    private interactionId?: string;
    private storageSessionIdKey = 'chatbot-session-id';

    constructor() {
        this.chatbotService = new ChatBotService();
    }

    async componentWillLoad() {
        this.escalation = await this.chatbotService.getEscalation();
        this.texts = await this.chatbotService.getTexts();
    }

    componentDidLoad() {
        const desktopBreakpoint = window.getComputedStyle(this.host).getPropertyValue('--web-chatbot-desktop-vp');
        this.isDesktop = !!window.matchMedia(`(min-width: ${desktopBreakpoint})`).matches;
    }

    private getSessionId = (): string => storage.getItem(this.storageSessionIdKey) as string;

    private saveIds = (sessionId: string, interactionId: string) => {
        storage.setItem(this.storageSessionIdKey, sessionId);
        this.interactionId = interactionId;
    };

    private fetchAnswer = async (
        eventOrQuestion: CustomEvent<string> | string,
        sessionId?: string,
        dialogPath?: string
    ): Promise<void> => {
        this.submitDisabled = true;

        let question: string;
        if (eventOrQuestion instanceof CustomEvent) {
            question = eventOrQuestion.detail;
        } else {
            question = eventOrQuestion;
        }

        this.addQuestionToList(question);

        const response = await this.chatbotService.askQuestion(question, sessionId, dialogPath);
        this.addAnswerToList(response);

        if (response instanceof Object) {
            this.saveIds(response.sessionId, response.interactionId);
        }

        this.submitDisabled = false;

        return Promise.resolve();
    };

    private forwardToOverlay = (event: CustomEvent<string>) => {
        this.openChatbot();
        this.fetchAnswer(event);
    };

    private onDialogOptionClicked = (
        event: WebChatbotListCustomEvent<{ sessionId: string; value: ChatbotDialogOptionInterface }>
    ) => {
        const {
            sessionId,
            value: { path, text }
        } = event.detail;
        this.fetchAnswer(text, sessionId, path);
    };

    private addQuestionToList = (question: string) => {
        this.chatBotList = [...this.chatBotList, question];
    };

    private addAnswerToList = (answer: string | ChatbotAnswerInterface) => {
        this.chatBotList = [...this.chatBotList, answer];
    };

    private onFeedback = async ({ detail: feedback }: { detail: Omit<ChatbotEscalationFeedbackInterface, 'q'> }) => {
        const { question: lastQuestion } = this.chatBotList.at(-1) as ChatbotAnswerInterface;
        await this.chatbotService.sendFeedback(this.getSessionId(), this.interactionId ?? '', {
            ...feedback,
            ...{ q: lastQuestion }
        });
    };

    private onScroll = () => {
        // By default the whole page scrolls, in portable mode the questions scroll separately.
        const inOverlay = this.mode !== 'normal';
        const selector = inOverlay ? '.chatbot-list > *:last-child' : '.chatbot-input-area';
        const scrollTarget = this.host.querySelector(selector);
        scrollTarget?.scrollIntoView({ block: 'end', behavior: 'smooth' });
    };

    private onOverlayToggle = ({ detail }) => {
        if (detail?.toggle) {
            detail.overlay?.querySelector('web-chatbot-input input[type="text"]')?.focus();
        }
    };

    private renderChatbotContent = () => (
        <Fragment>
            <web-chatbot-list
                items={this.chatBotList}
                onDialogOption={this.onDialogOptionClicked}
                onShouldScroll={this.onScroll}
                escalation={this.escalation}
                onFeedback={this.onFeedback}
                texts={this.texts}
            ></web-chatbot-list>
            <div class="chatbot-input-area">
                <web-chatbot-input
                    maxlength={this.maxlength}
                    maxLengthText={this.maxLengthText}
                    submitButtonText={this.submitButtonText}
                    placeholderText={this.placeholderText}
                    onValueEmitted={this.fetchAnswer}
                    submitButtonEnabled={!this.submitDisabled}
                ></web-chatbot-input>
                <slot name="footer"></slot>
            </div>
        </Fragment>
    );

    private openChatbot() {
        const overlay = this.host.querySelector('web-overlay');
        overlay?.open();
    }

    private renderChatButton = () => (
        <button
            aria-label={this.chatbotAriaLabelOpen}
            class={`chatbot-button mobile-menu`}
            onClick={() => this.openChatbot()}
        >
            <web-icon name="chat"></web-icon>
        </button>
    );

    private renderInDialog = () =>
        this.isDesktop ? (
            this.renderChatButton()
        ) : (
            <web-visibility-trigger target-element-id="footer">{this.renderChatButton()}</web-visibility-trigger>
        );

    private renderInDrawer = () => (
        <web-chatbot-small
            placeholderText={this.placeholderText}
            submitButtonText={this.submitButtonText}
            startup-text={this.texts.startupText}
            maxLengthText={this.maxLengthText}
            onValueChanged={this.forwardToOverlay}
        ></web-chatbot-small>
    );

    private renderChatbot() {
        switch (this.mode) {
            case 'normal':
                return <div class="chatbot-container">{this.renderChatbotContent()}</div>;

            case 'portable':
                return this.renderInDialog();

            case 'small':
                return this.renderInDrawer();
        }
    }

    render = () => (
        <Host>
            {this.renderChatbot()}

            {this.mode !== 'normal' && (
                <web-overlay
                    modal={false}
                    close-button-text={this.closeButtonText}
                    onOverlayToggle={this.onOverlayToggle}
                >
                    <div class="chatbot-overlay">{this.renderChatbotContent()}</div>
                </web-overlay>
            )}
        </Host>
    );
}
